import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import {
  calculateBulkSubtotal,
  calculateQuantityFromAmount,
  convertToProductUnit,
  formatBulkQuantity,
  formatPricePerUnit,
  getInputUnitsForProduct,
  normalizeMeasurementUnit,
} from '../../utils/bulkSaleUtils';
import './BulkSaleModal.css';

const formatMoney = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '$0,00';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value));
};

const BulkSaleModalForm = ({
  product,
  testIdPrefix,
  mode,
  setMode,
  inputUnit,
  setInputUnit,
  quantityInput,
  amountInput,
  onQuantityChange,
  onAmountChange,
  onInputUnitChange,
  resolvedQuantity,
  resolvedSubtotal,
  productUnit,
  unitOptions,
  quantityInProductUnit,
  quantityFromAmount,
  unitPrice,
}) => (
  <>
    <p className="carta-v7-bulk-modal__product-name">{product.name}</p>
    {product.brandName && (
      <p className="carta-v7-bulk-modal__brand">Marca: {product.brandName}</p>
    )}
    <p className="carta-v7-bulk-modal__hint">
      Precio de referencia: <strong>{formatPricePerUnit(unitPrice, productUnit)}</strong>
    </p>

    <div className="carta-v7-bulk-modal__tabs" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'quantity'}
        className={`carta-v7-bulk-modal__tab${mode === 'quantity' ? ' carta-v7-bulk-modal__tab--active' : ''}`}
        onClick={() => setMode('quantity')}
        data-testid={`${testIdPrefix}-tab-quantity`}
      >
        Por cantidad
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'amount'}
        className={`carta-v7-bulk-modal__tab${mode === 'amount' ? ' carta-v7-bulk-modal__tab--active' : ''}`}
        onClick={() => setMode('amount')}
        data-testid={`${testIdPrefix}-tab-amount`}
      >
        Por monto
      </button>
    </div>

    {mode === 'quantity' ? (
      <div className="carta-v7-bulk-modal__field">
        <label htmlFor={`${testIdPrefix}-quantity-input`} className="carta-v7-bulk-modal__label">
          ¿Cuánto querés? <span className="text-danger">*</span>
        </label>
        <div className="carta-v7-bulk-modal__input-row">
          <input
            id={`${testIdPrefix}-quantity-input`}
            type="number"
            min="0"
            step="0.001"
            className="form-control"
            placeholder="Ej: 0,5"
            value={quantityInput}
            onChange={(e) => onQuantityChange(e.target.value)}
            data-testid={`${testIdPrefix}-quantity-input`}
          />
          {unitOptions.length > 1 ? (
            <select
              className="form-select carta-v7-bulk-modal__unit-select"
              value={inputUnit}
              onChange={(e) => onInputUnitChange(e.target.value)}
              data-testid={`${testIdPrefix}-unit-select`}
            >
              {unitOptions.map((u) => (
                <option key={u.value} value={u.value}>{u.value}</option>
              ))}
            </select>
          ) : (
            <span className="carta-v7-bulk-modal__unit-badge">{productUnit}</span>
          )}
        </div>
        {quantityInProductUnit > 0 && inputUnit !== productUnit && (
          <small className="carta-v7-bulk-modal__equiv">
            = {formatBulkQuantity(quantityInProductUnit, productUnit)} en el producto
          </small>
        )}
      </div>
    ) : (
      <div className="carta-v7-bulk-modal__field">
        <label htmlFor={`${testIdPrefix}-amount-input`} className="carta-v7-bulk-modal__label">
          ¿Cuánto querés cobrar? <span className="text-danger">*</span>
        </label>
        <div className="carta-v7-bulk-modal__input-row">
          <span className="carta-v7-bulk-modal__currency">$</span>
          <input
            id={`${testIdPrefix}-amount-input`}
            type="number"
            min="0"
            step="0.01"
            className="form-control"
            placeholder="Ej: 1500"
            value={amountInput}
            onChange={(e) => onAmountChange(e.target.value)}
            data-testid={`${testIdPrefix}-amount-input`}
          />
        </div>
        {quantityFromAmount > 0 && (
          <small className="carta-v7-bulk-modal__equiv">
            Equivale a {formatBulkQuantity(quantityFromAmount, productUnit)}
          </small>
        )}
      </div>
    )}

    <div className="carta-v7-bulk-modal__summary" data-testid={`${testIdPrefix}-summary`}>
      <div>
        <span>Cantidad</span>
        <strong>{resolvedQuantity > 0 ? formatBulkQuantity(resolvedQuantity, productUnit) : '—'}</strong>
      </div>
      <div>
        <span>Subtotal</span>
        <strong>{resolvedSubtotal > 0 ? formatMoney(resolvedSubtotal) : '—'}</strong>
      </div>
    </div>
  </>
);

/**
 * Modal unificado para venta a granel (carta, mostrador, caja).
 * onConfirm recibe { bulkQuantity, subTotal, measurementUnit }.
 */
const BulkSaleModal = ({
  product,
  show,
  onClose,
  onConfirm,
  variant = 'bootstrap',
  testIdPrefix = 'bulk-sale-modal',
  confirmLabel = 'Agregar',
}) => {
  const [mode, setMode] = useState('quantity');
  const [inputUnit, setInputUnit] = useState('kg');
  const [quantityInput, setQuantityInput] = useState('');
  const [amountInput, setAmountInput] = useState('');

  const productUnit = normalizeMeasurementUnit(product?.measurementUnit);
  const unitOptions = useMemo(
    () => (product ? getInputUnitsForProduct(product.measurementUnit) : []),
    [product]
  );

  useEffect(() => {
    if (!show || !product) return;
    setMode('quantity');
    setQuantityInput('');
    setAmountInput('');
    const defaultInput =
      unitOptions.find((u) => u.value === productUnit)?.value || unitOptions[0]?.value || 'kg';
    setInputUnit(defaultInput);
  }, [show, product, productUnit, unitOptions]);

  if (!show || !product) return null;

  const unitPrice = Number(product.price);
  const quantityInProductUnit = quantityInput
    ? convertToProductUnit(parseFloat(quantityInput), inputUnit, productUnit)
    : 0;
  const subtotalFromQuantity = calculateBulkSubtotal(unitPrice, quantityInProductUnit);
  const subtotalFromAmount = amountInput ? Number(parseFloat(amountInput).toFixed(2)) : 0;
  const quantityFromAmount = amountInput
    ? calculateQuantityFromAmount(unitPrice, parseFloat(amountInput))
    : 0;
  const resolvedQuantity = mode === 'quantity' ? quantityInProductUnit : quantityFromAmount;
  const resolvedSubtotal = mode === 'quantity' ? subtotalFromQuantity : subtotalFromAmount;
  const canConfirm = resolvedQuantity > 0 && resolvedSubtotal > 0;

  const handleQuantityChange = (value) => {
    setMode('quantity');
    setQuantityInput(value);
    const qty = value ? convertToProductUnit(parseFloat(value), inputUnit, productUnit) : 0;
    setAmountInput(qty > 0 ? String(calculateBulkSubtotal(unitPrice, qty)) : '');
  };

  const handleAmountChange = (value) => {
    setMode('amount');
    setAmountInput(value);
    const qty = value ? calculateQuantityFromAmount(unitPrice, parseFloat(value)) : 0;
    setQuantityInput(qty > 0 ? String(qty) : '');
  };

  const handleInputUnitChange = (newUnit) => {
    setInputUnit(newUnit);
    if (quantityInput) {
      const qty = convertToProductUnit(parseFloat(quantityInput), newUnit, productUnit);
      setAmountInput(qty > 0 ? String(calculateBulkSubtotal(unitPrice, qty)) : '');
    }
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      bulkQuantity: resolvedQuantity,
      subTotal: resolvedSubtotal,
      measurementUnit: productUnit,
    });
  };

  const formProps = {
    product,
    testIdPrefix,
    mode,
    setMode,
    inputUnit,
    setInputUnit,
    quantityInput,
    amountInput,
    onQuantityChange: handleQuantityChange,
    onAmountChange: handleAmountChange,
    onInputUnitChange: handleInputUnitChange,
    resolvedQuantity,
    resolvedSubtotal,
    productUnit,
    unitOptions,
    quantityInProductUnit,
    quantityFromAmount,
    unitPrice,
  };

  const actions = (
    <div className="carta-v7-bulk-modal__actions">
      <button
        type="button"
        className="carta-v7-bulk-modal__btn carta-v7-bulk-modal__btn--secondary"
        onClick={onClose}
        data-testid={`${testIdPrefix}-cancel-btn`}
      >
        Cancelar
      </button>
      <button
        type="button"
        className="carta-v7-bulk-modal__btn carta-v7-bulk-modal__btn--primary"
        disabled={!canConfirm}
        onClick={handleConfirm}
        data-testid={`${testIdPrefix}-confirm-btn`}
      >
        {confirmLabel}
      </button>
    </div>
  );

  if (variant === 'carta') {
    return (
      <div
        className="carta-v7-bulk-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testIdPrefix}-title`}
        data-testid={testIdPrefix}
        onClick={onClose}
      >
        <div className="carta-v7-bulk-modal__sheet" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="carta-v7-bulk-modal__close"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid={`${testIdPrefix}-close-btn`}
          >
            ×
          </button>
          <h2 id={`${testIdPrefix}-title`} className="carta-v7-bulk-modal__title">
            Venta a granel
          </h2>
          <BulkSaleModalForm {...formProps} />
          {actions}
        </div>
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onClose} data-testid={testIdPrefix}>
      <Modal.Header closeButton>
        <Modal.Title>Venta a granel</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bulk-sale-modal-bootstrap">
        <BulkSaleModalForm {...formProps} />
      </Modal.Body>
      <Modal.Footer className="d-flex gap-2">
        <Button variant="secondary" onClick={onClose} data-testid={`${testIdPrefix}-cancel-btn`}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={!canConfirm}
          onClick={handleConfirm}
          data-testid={`${testIdPrefix}-confirm-btn`}
        >
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkSaleModal;
